import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Sparkles, Send, Bot, User, X as XIcon, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = (card) => `customer_ai_chat_${card}`;

const QUICK = [
  "Quanti punti mi mancano al prossimo premio?",
  "Quali coupon ho attivi?",
  "Che orari fa la mia sede preferita?",
];

/**
 * Floating chatbot widget per l'area cliente (portale OTP-protetto).
 * Passare `card`, `sessionToken`, `primary` (accent color) e `visible`.
 */
export default function CustomerAiChat({ card, sessionToken, primary = "#E5383B", visible = true, bottomOffset = 0 }) {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => {
    let s = null;
    try { s = localStorage.getItem(LS_KEY(card)); } catch (_) {}
    if (!s) {
      s = Math.random().toString(36).slice(2, 12);
      try { localStorage.setItem(LS_KEY(card), s); } catch (_) {}
    }
    return s;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending || !sessionToken) return;
    setMessages((prev) => [...prev, { role: "user", content: message }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/ai/customer-chat`, {
        card_number: card, session_token: sessionToken,
        session_id: sessionId, message,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: data.text || "" }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Servizio non disponibile";
      setMessages((prev) => [...prev, { role: "assistant", content: `⚠️ ${detail}` }]);
    } finally { setSending(false); }
  };

  if (!visible || !card || !sessionToken) return null;

  return (
    <>
      {/* FAB */}
      {!open && (
        <button
          data-testid="customer-ai-fab"
          onClick={() => setOpen(true)}
          className="fixed right-4 z-40 h-14 w-14 rounded-full shadow-lg flex items-center justify-center text-white hover:scale-105 transition"
          style={{ background: primary, bottom: 16 + bottomOffset }}
          aria-label="Apri assistente"
        >
          <Sparkles className="h-6 w-6" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          style={{ bottom: 16 + bottomOffset }} className="fixed right-4 z-40 w-[92vw] sm:w-[380px] max-h-[75vh] rounded-2xl border border-zinc-800 bg-[color:var(--bg-surface,#141416)] shadow-2xl overflow-hidden flex flex-col"
          data-testid="customer-ai-panel"
        >
          <div className="p-3 flex items-center justify-between border-b border-zinc-800" style={{ background: primary }}>
            <div className="flex items-center gap-2 text-white">
              <Bot className="h-4 w-4" />
              <span className="text-sm font-bold">Assistente Fedeltà</span>
            </div>
            <button data-testid="customer-ai-close" onClick={() => setOpen(false)} className="text-white/80 hover:text-white">
              <XIcon className="h-4 w-4" />
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[280px]" data-testid="customer-ai-messages">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <Bot className="h-6 w-6 mx-auto text-zinc-500 mb-2" />
                <p className="text-xs text-zinc-400 mb-3">Chiedimi dei tuoi punti, premi o coupon.</p>
                <div className="flex flex-col gap-1.5">
                  {QUICK.map((q, i) => (
                    <button key={i} onClick={() => send(q)}
                      data-testid={`customer-ai-quick-${i}`}
                      className="text-left text-[12px] px-3 py-1.5 rounded-md bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-200">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-1.5 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && <div className="h-6 w-6 rounded-full bg-zinc-800 text-zinc-300 flex items-center justify-center shrink-0"><Bot className="h-3 w-3" /></div>}
                <div className={`px-2.5 py-1.5 rounded-xl max-w-[80%] whitespace-pre-wrap text-[13px] leading-snug ${
                  m.role === "user" ? "text-white" : "bg-zinc-900 border border-zinc-800 text-zinc-100"
                }`} style={m.role === "user" ? { background: primary } : {}}>
                  {m.content}
                </div>
                {m.role === "user" && <div className="h-6 w-6 rounded-full bg-zinc-800 text-zinc-300 flex items-center justify-center shrink-0"><User className="h-3 w-3" /></div>}
              </div>
            ))}
            {sending && (
              <div className="flex gap-1.5" data-testid="customer-ai-typing">
                <div className="h-6 w-6 rounded-full bg-zinc-800 text-zinc-300 flex items-center justify-center"><Bot className="h-3 w-3" /></div>
                <div className="px-2.5 py-1.5 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs">
                  <RefreshCw className="h-3 w-3 inline animate-spin mr-1" /> …
                </div>
              </div>
            )}
          </div>

          <div className="p-2 border-t border-zinc-800">
            <div className="flex gap-1.5">
              <Textarea
                data-testid="customer-ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Scrivi qui…"
                rows={1}
                className="bg-zinc-900 border-zinc-800 text-sm resize-none min-h-[36px]"
              />
              <Button data-testid="customer-ai-send" onClick={() => send()} disabled={sending || !input.trim()}
                size="sm" className="h-9 shrink-0 text-white" style={{ background: primary }}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
